import axios from 'axios';

// Ensure API URL is set - fail fast if not configured
const API_URL = import.meta.env.VITE_API_URL;
if (!API_URL) {
  console.error('VITE_API_URL is not set! Check your environment configuration.');
  throw new Error('VITE_API_URL environment variable is required');
}

class DocumentService {
  // Get document categories
  async getCategories() {
    try {
      const response = await axios.get(`${API_URL}/api/documents/categories`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching document categories:', error);
      throw error;
    }
  }

  // Presigned upload flow (NEW - primary method)
  async uploadDocumentPresigned(file, scopeType, category, description, confirmationNumber, tripId = null) {
    try {
      // Step 1: Get presigned upload URL
      const presignResponse = await axios.post(
        `${API_URL}/api/documents/presign-upload`,
        {
          scopeType,
          fileName: file.name,
          mimeType: file.type,
          category,
          description,
          confirmationNumber,
          tripId: scopeType === 'TRIP' ? tripId : undefined,
          sizeBytes: file.size
        },
        { withCredentials: true }
      );

      const { uploadUrl, storageKey } = presignResponse.data.data;

      // Step 2: Upload file directly to S3 using presigned URL.
      // Content-Type must match exactly what was used to sign the URL (avoids 403 SignatureDoesNotMatch).
      // The presign request included file.type; we must send the same value.
      const contentType = file.type || 'application/octet-stream';
      const s3Response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': contentType
        },
        body: file
      });

      if (!s3Response.ok) {
        throw new Error(`S3 upload failed: ${s3Response.statusText}`);
      }

      // Step 3: Complete upload (store metadata in database)
      const completeResponse = await axios.post(
        `${API_URL}/api/documents/complete`,
        {
          storageKey,
          scopeType,
          fileName: file.name,
          mimeType: file.type,
          category,
          description,
          confirmationNumber,
          tripId: scopeType === 'TRIP' ? tripId : undefined,
          sizeBytes: file.size
        },
        { withCredentials: true }
      );

      return completeResponse.data;
    } catch (error) {
      console.error('Error uploading document (presigned flow):', error);
      throw error;
    }
  }

  // Legacy upload (kept for backward compatibility)
  async uploadDocument(file, category, description, confirmationNumber, tripId = null, clientId = null) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      if (description) formData.append('description', description);
      if (confirmationNumber) formData.append('confirmationNumber', confirmationNumber);
      // For agency-level uploads, tripId and clientId are omitted
      if (tripId) formData.append('tripId', tripId);
      if (clientId) formData.append('clientId', clientId);

      const response = await axios.post(`${API_URL}/api/documents/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  }

  // Get all documents for the agency
  async getAllDocuments(category = null, scopeType = null, tripId = null) {
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (scopeType) params.append('scopeType', scopeType);
      if (tripId) params.append('tripId', tripId);

      const response = await axios.get(
        `${API_URL}/api/documents?${params.toString()}`,
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw error;
    }
  }

  // Get documents for a trip
  async getDocuments(tripId, category = null, clientId = null) {
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (clientId) params.append('clientId', clientId);

      const response = await axios.get(
        `${API_URL}/api/documents/trip/${tripId}?${params.toString()}`,
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw error;
    }
  }

  // Get document download URL
  async getDownloadUrl(documentId, expiresIn = 3600) {
    try {
      const response = await axios.get(
        `${API_URL}/api/documents/${documentId}/download?expiresIn=${expiresIn}`,
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting download URL:', error);
      throw error;
    }
  }

  // Update document metadata
  async updateDocument(documentId, updates) {
    try {
      const response = await axios.patch(
        `${API_URL}/api/documents/${documentId}`,
        updates,
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating document:', error);
      throw error;
    }
  }

  // Delete document
  async deleteDocument(documentId) {
    try {
      const response = await axios.delete(
        `${API_URL}/api/documents/${documentId}`,
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  }

  // Download document (opens in new tab)
  async downloadDocument(documentId) {
    try {
      const { data } = await this.getDownloadUrl(documentId);
      window.open(data.downloadUrl, '_blank');
    } catch (error) {
      console.error('Error downloading document:', error);
      throw error;
    }
  }

  // Format file size
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Get file icon based on MIME type
  getFileIcon(mimeType) {
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('image')) return '🖼️';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
    if (mimeType.includes('text')) return '📄';
    return '📎';
  }
}

export default new DocumentService();
